export function hexToRgba(hexCode: string, opacity: number = 1) {
  let hex = hexCode.replace("#", "");

  if (hex.length === 3) {
    hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 ** Convert hex color to HSB (Hue, Saturation, Brightness)
 */
function getBrightness(hex: string, assumedBgBrightness: number = 255) {
  // Remove # if present
  hex = hex.replace("#", "");

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Get alpha (default to fully opaque if not present)
  const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;

  // Calculate foreground brightness
  const fgBrightness = 0.299 * r + 0.587 * g + 0.114 * b;

  // Blend with assumed background based on alpha
  // Formula: result = (fg * alpha) + (bg * (1 - alpha))
  return fgBrightness * a + assumedBgBrightness * (1 - a);
}

/**
 ** Get text color based on background brightness
 ** If brightness > 65%: returns zinc-800 with 50% opacity
 ** If brightness <= 65%: returns white with 40% opacity
 **
 ** @param bgHexColor - Hex color with optional alpha (#RRGGBB or #RRGGBBAA)
 ** @param assumedBgBrightness - Brightness of the background behind the transparent color (0-255, default 255 for white)
 */
export const getTextColorByBrightness = ({
  bgHexColor,
  assumedBgBrightness = 255,
  darkOpacity = 12,
  lightOpacity = 24,
}: {
  bgHexColor: string;
  assumedBgBrightness?: number;
  darkOpacity?: number;
  lightOpacity?: number;
}) => {
  const brightness = getBrightness(bgHexColor, assumedBgBrightness);

  // 65% of 255 = ~166
  if (brightness > 166) {
    // zinc-800 (#27272a)
    return `rgba(39, 39, 42, ${darkOpacity}%)`;
  } else {
    // white
    return `rgba(255, 255, 255, ${lightOpacity}%)`;
  }
};

/**
 ** Whether a background is dark enough to need light foreground colors.
 ** Uses the same 65% brightness threshold as getTextColorByBrightness.
 ** Non-hex values (CSS variables, named colors) are treated as light, since white
 ** foregrounds on an unknown background are the riskier default.
 **
 ** @param bgHexColor - Hex color with optional alpha (#RRGGBB or #RRGGBBAA)
 ** @param assumedBgBrightness - Brightness of the background behind the transparent color (0-255, default 255 for white)
 */
export const isDarkBackgroundColor = (bgHexColor?: string, assumedBgBrightness: number = 255) => {
  if (!bgHexColor) return false;

  const brightness = getBrightness(bgHexColor, assumedBgBrightness);

  return Number.isFinite(brightness) && brightness <= 166;
};

/**
 * Gets CSS variable value and returns a style object if the variable is defined
 * @param cssVariable - The CSS variable name (e.g., '--flo-primary-button-color')
 * @param styleProperty - The CSS property to apply (e.g., 'backgroundColor', 'color')
 * @returns Style object with the property set to the CSS variable, or empty object if undefined
 */
export const getStyleFromCSSVariable = (
  cssVariable: string,
  styleProperty: keyof React.CSSProperties,
): React.CSSProperties => {
  if (typeof window === "undefined") return {};

  const computedStyles = getComputedStyle(document.documentElement);
  const value = computedStyles.getPropertyValue(cssVariable).trim();

  if (value) {
    return { [styleProperty]: `var(${cssVariable})` } as React.CSSProperties;
  }

  return {};
};
